import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
} from "@coreui/react"
import { Flex, Input, Typography, Select, InputNumber, Radio, Space } from "antd"
import { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  getTypesForPointsRulesAction,
  addPointsRulesActions,
  editPointsRulesActions,
  getPointsRulesActions,
} from "src/store/Slices/UsersRatingSlice"

const AddEditModal = ({ visible, setVisible, setPage, setPageSize }) => {
  const dispatch = useDispatch()

  const statusItems = [
    { id: true, label: "Активен" },
    { id: false, label: "Не активен" },
  ]

  const timeUnits = [
    { label: "Минуты", value: "minutes" },
    { label: "Часы", value: "hours" },
  ]

  const [formData, setFormData] = useState({
    actionTypeId: null,
    actionTypeName: null,
    points: "",
    minutesFromLastAction: "",
    isActive: true,
  })

  const [selectedTimeUnit, setSelectedTimeUnit] = useState("minutes")
  const [inputTimeValue, setInputTimeValue] = useState(null)

  const typesForPointsRulesAction = useSelector(
    (state) => state.UsersRatingSlice?.typesForPointsRulesAction,
  )

  useEffect(() => {
    if (visible.open) {
      dispatch(getTypesForPointsRulesAction())
    }
  }, [visible.open, dispatch])

  useEffect(() => {
    if (visible.flag === "edit" && visible.ruleData) {
      const minutes = visible.ruleData.minutesFromLastAction || 0
      
      setFormData({
        actionTypeId: visible.ruleData.actionTypeId,
        actionTypeName: visible.ruleData.actionTypeName,
        points: visible.ruleData.points || "",
        minutesFromLastAction: minutes,
        isActive: visible.ruleData.isActive !== undefined ? visible.ruleData.isActive : true,
      })

      // Определяем единицы измерения и устанавливаем значение
      if (minutes > 0) {
        if (minutes >= 60 && minutes % 60 === 0) {
          setSelectedTimeUnit("hours")
          setInputTimeValue(minutes / 60)
        } else {
          setSelectedTimeUnit("minutes")
          setInputTimeValue(minutes)
        }
      } else {
        setSelectedTimeUnit("minutes")
        setInputTimeValue(null)
      }
    }
  }, [visible.flag, visible.ruleData])

  useEffect(() => {
    if (visible.flag === "add" && visible.open) {
      setFormData({
        actionTypeId: null,
        actionTypeName: null,
        points: "",
        minutesFromLastAction: "",
        isActive: true,
      })
      setSelectedTimeUnit("minutes")
      setInputTimeValue(null)
    }
  }, [visible.flag, visible.open])

  const selectOptions = typesForPointsRulesAction?.data?.value?.map((item) => ({
    label: item.label,
    value: item.label,
  })) || []

  const statusOptions = statusItems.map((item) => ({
    label: item.label,
    value: item.id,
  }))

  const handleTimeValueChange = (value) => {
    setInputTimeValue(value)
    
    // Конвертируем в минуты в зависимости от выбранной единицы
    let minutes = 0
    if (value && value > 0) {
      if (selectedTimeUnit === "hours") {
        minutes = value * 60
      } else {
        minutes = value
      }
    }
    
    setFormData((prev) => ({
      ...prev,
      minutesFromLastAction: minutes,
    }))
  }

  const handleTimeUnitChange = (e) => {
    const newUnit = e.target.value
    setSelectedTimeUnit(newUnit)
    
    // При переключении единицы измерения конвертируем текущее значение
    if (inputTimeValue && inputTimeValue > 0) {
      let minutes = 0
      if (newUnit === "hours") {
        // Конвертируем минуты в часы
        const hours = inputTimeValue / 60
        setInputTimeValue(hours)
        minutes = inputTimeValue
      } else {
        // Конвертируем часы в минуты
        const minutesValue = inputTimeValue * 60
        setInputTimeValue(minutesValue)
        minutes = minutesValue
      }
      
      setFormData((prev) => ({
        ...prev,
        minutesFromLastAction: minutes,
      }))
    }
  }

  const handleSubmit = () => {
    const selectedType = typesForPointsRulesAction?.data?.value?.find(
      (item) => item.label === formData.actionTypeName
    )

    const submitData = {
      actionTypeId: selectedType?.value || formData.actionTypeId,
      points: formData.points,
      minutesFromLastAction: formData.minutesFromLastAction,
      isActive: formData.isActive,
    }

    switch (visible.flag) {
      case "add": {
        dispatch(addPointsRulesActions(submitData)).then(() => {
          dispatch(getPointsRulesActions())
          setVisible({ open: false, flag: "", ruleData: null })
          setPage(1)
          setPageSize(20)
        })
        break
      }
      case "edit": {
        const editData = {
          id: visible.ruleData?.id,
          ...submitData,
        }
        dispatch(editPointsRulesActions(editData)).then(() => {
          dispatch(getPointsRulesActions())
          setVisible({ open: false, flag: "", ruleData: null })
        })
        break
      }
      default:
        break
    }
  }

  const handleClose = () => {
    setVisible({ open: false, flag: "", ruleData: null })
  }

  return (
    <CModal
      alignment='center'
      visible={visible.open}
      onClose={handleClose}
      keyboard={true}
    >
      <CModalHeader>
        <CModalTitle>
          {visible.flag === "add"
            ? "Добавление правил начисления бонусов"
            : "Редактирование правил начисления бонусов"}
        </CModalTitle>
      </CModalHeader>
      <CModalBody
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Flex vertical gap={16} style={{ marginTop: "20px" }}>
          <div>
            <Typography.Title level={5}>Тип действия</Typography.Title>
            <Select
              options={selectOptions}
              value={formData.actionTypeName}
              onChange={(value) =>
                setFormData({ ...formData, actionTypeName: value })
              }
              placeholder='Выберите тип действия'
              style={{ width: "100%" }}
              showSearch
              optionFilterProp='label'
            />
          </div>

          <div>
            <Typography.Title level={5}>Количество баллов</Typography.Title>
            <Input
              type='number'
              value={formData.points}
              onChange={(e) =>
                setFormData({ ...formData, points: e.target.value })
              }
              placeholder='Введите количество баллов'
              min={0}
            />
          </div>

          <div>
            <Typography.Title level={5}>Частота периода</Typography.Title>
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <Radio.Group
                options={timeUnits}
                onChange={handleTimeUnitChange}
                value={selectedTimeUnit}
                optionType="button"
                buttonStyle="solid"
                style={{ width: "100%" }}
              />
              
              <InputNumber
                value={inputTimeValue}
                onChange={handleTimeValueChange}
                placeholder={`Введите количество ${selectedTimeUnit === "hours" ? "часов" : "минут"}`}
                style={{ width: "100%" }}
                min={0}
                precision={0}
              />
            </Space>
          </div>

          <div>
            <Typography.Title level={5}>Статус</Typography.Title>
            <Select
              options={statusOptions}
              value={formData.isActive}
              onChange={(value) =>
                setFormData({ ...formData, isActive: value })
              }
              placeholder='Выберите статус'
              style={{ width: "100%" }}
            />
          </div>
        </Flex>
      </CModalBody>
      <CModalFooter>
        <CButton
          onClick={handleClose}
          style={{ color: "#fff" }}
          color='secondary'
        >
          Закрыть
        </CButton>
        <CButton onClick={handleSubmit} style={{ color: "#fff" }}>
          {visible.flag === "add" ? "Добавить" : "Сохранить"}
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default AddEditModal